import { useEffect, useRef, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bot, KeyRound, RotateCcw, Send, Sunrise, Trash2 } from "lucide-react";
import { assistantApi } from "../lib/api";
import { useI18n } from "../lib/i18n";
import Select from "../components/Select";

export default function AssistantPage() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const settingsQ = useQuery({
    queryKey: ["assistant-settings"],
    queryFn: () => assistantApi.settings(),
  });
  const historyQ = useQuery({
    queryKey: ["assistant-history"],
    queryFn: () => assistantApi.history(),
    enabled: settingsQ.data?.configured ?? false,
  });

  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const chatM = useMutation({
    mutationFn: (m: string) => assistantApi.chat(m),
    onSuccess: () => {
      setMessage("");
      qc.invalidateQueries({ queryKey: ["assistant-history"] });
    },
    onError: (e) => setError((e as Error).message),
  });

  const resetM = useMutation({
    mutationFn: () => assistantApi.newThread(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["assistant-history"] }),
  });

  const briefingM = useMutation({
    mutationFn: () => assistantApi.briefing(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["assistant-history"] }),
    onError: (e) => setError((e as Error).message),
  });

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [historyQ.data]);

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (message.trim()) chatM.mutate(message.trim());
  }

  return (
    <>
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-parchment-muted text-xs uppercase tracking-widest">{t("nav.assistant")}</p>
          <h1 className="text-3xl font-bold text-parchment mt-1 flex items-center gap-2">
            <Bot className="w-6 h-6 text-accent" />
            {t("assistant.title")}
          </h1>
        </div>
        {settingsQ.data?.configured && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => briefingM.mutate()}
              disabled={briefingM.isPending}
              className="btn-gold"
              title={t("assistant.briefingTitle")}
            >
              <Sunrise className="w-4 h-4" />
              {briefingM.isPending ? t("assistant.briefingPending") : t("assistant.dailyBriefing")}
            </button>
            <button
              type="button"
              onClick={() => {
                if (confirm(t("assistant.resetConfirm"))) resetM.mutate();
              }}
              disabled={resetM.isPending}
              className="btn-ghost"
            >
              <RotateCcw className="w-4 h-4" />
              {t("assistant.newThread")}
            </button>
          </div>
        )}
      </header>

      <SettingsPanel />

      {settingsQ.data?.configured && (
        <section className="glass flex flex-col p-0 overflow-hidden" style={{ minHeight: "60vh" }}>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
            {(historyQ.data?.messages.length ?? 0) === 0 && (
              <p className="text-parchment-muted text-sm text-center py-8">
                {t("assistant.emptyState")}
              </p>
            )}
            {(historyQ.data?.messages ?? []).map((m) => (
              <div
                key={m.id}
                className={
                  "max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap " +
                  (m.role === "user"
                    ? "ms-auto bg-gold-500/15 border border-gold-500/25 text-parchment"
                    : "me-auto bg-surface-1 border border-glass text-parchment")
                }
              >
                {m.content}
              </div>
            ))}
            {chatM.isPending && (
              <div className="me-auto bg-surface-1 border border-glass rounded-2xl px-4 py-3 text-sm text-parchment-muted animate-pulse">
                {t("assistant.thinking")}
              </div>
            )}
          </div>

          <form
            onSubmit={submit}
            className="border-t border-glass p-3 flex items-end gap-2 bg-obsidian-950/40"
          >
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  submit(e as unknown as FormEvent);
                }
              }}
              rows={2}
              placeholder={t("assistant.inputPlaceholder")}
              className="input flex-1 resize-none"
            />
            <button
              type="submit"
              disabled={chatM.isPending || !message.trim()}
              className="btn-gold self-stretch"
            >
              <Send className="w-4 h-4" />
              {t("assistant.send")}
            </button>
          </form>
          {error && (
            <p className="px-4 pb-3 text-sm text-pnl-neg">{error}</p>
          )}
        </section>
      )}
    </>
  );
}

function SettingsPanel() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["assistant-settings"], queryFn: () => assistantApi.settings() });
  const [open, setOpen] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [model, setModel] = useState("gpt-4o");
  const [instructions, setInstructions] = useState("");
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (q.data) {
      setModel(q.data.model || "gpt-4o");
      setInstructions(q.data.coach_instructions ?? "");
      // Auto-open if not configured yet.
      if (!q.data.configured) setOpen(true);
    }
  }, [q.data]);

  const saveM = useMutation({
    mutationFn: () =>
      assistantApi.saveSettings({
        api_key: apiKey.trim() || undefined,
        model,
        coach_instructions: instructions,
      }),
    onSuccess: () => {
      setApiKey("");
      setMsg(t("assistant.savedMsg"));
      qc.invalidateQueries({ queryKey: ["assistant-settings"] });
      setTimeout(() => setMsg(null), 2500);
    },
    onError: (e) => setMsg((e as Error).message),
  });

  const removeM = useMutation({
    mutationFn: () => assistantApi.saveSettings({ remove_key: true }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assistant-settings"] });
      setMsg(t("assistant.keyRemoved"));
      setTimeout(() => setMsg(null), 2500);
    },
  });

  return (
    <section className="glass p-5">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-parchment flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-accent" />
          {t("assistant.settingsTitle")}
          {q.data?.configured && (
            <span className="chip-gold text-[10px] px-2 py-0.5">{t("assistant.configured")}</span>
          )}
        </h2>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-sm text-parchment-muted hover:text-parchment"
        >
          {open ? t("common.close") : t("common.edit")}
        </button>
      </div>

      {open && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            saveM.mutate();
          }}
          className="mt-4 grid gap-4"
        >
          <label className="grid gap-2">
            <span className="label">{t("assistant.apiKey")}</span>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={q.data?.configured ? t("assistant.apiKeyUnchanged") : "sk-…"}
              className="input"
              autoComplete="off"
            />
          </label>
          <label className="grid gap-2">
            <span className="label">{t("assistant.model")}</span>
            <Select
              value={model}
              onChange={(v) => setModel(v)}
              options={["gpt-4o", "gpt-4o-mini", "gpt-4-turbo", "gpt-3.5-turbo"]}
            />
          </label>
          <label className="grid gap-2">
            <span className="label">{t("assistant.coachInstructions")}</span>
            <textarea
              rows={4}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder={t("assistant.coachPlaceholder")}
              className="input resize-y"
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <button type="submit" disabled={saveM.isPending} className="btn-gold">
              {saveM.isPending ? t("common.saving") : t("common.save")}
            </button>
            {q.data?.configured && (
              <button
                type="button"
                onClick={() => removeM.mutate()}
                disabled={removeM.isPending}
                className="btn-ghost text-pnl-neg"
              >
                <Trash2 className="w-4 h-4" />
                {t("assistant.removeKey")}
              </button>
            )}
            {msg && <span className="self-center text-sm text-parchment-muted">{msg}</span>}
          </div>
        </form>
      )}
    </section>
  );
}
